import { Flags } from "@oclif/core"
import { SentryTraced } from "@sentry/nestjs"
import { analyzeComponents } from "@supernovaio/code-analyzer"
import { z, ZodType } from "zod"

import { SentryCommand } from "../types/index.js"

const ImportComponentsConfigSchema = z
  .object({
    designSystemId: z.string(),
    importFrom: z.string(),
    rootDir: z.string(),
    versionId: z.string(),
  })
  .transform(data => ({
    ...data,
    rootDir: data.rootDir ?? process.cwd(),
  }))

type ImportComponentsConfig = z.infer<typeof ImportComponentsConfigSchema>

export default class ImportComponents extends SentryCommand<ImportComponentsConfig> {
  static override args = {}
  static override description = "Analyze react components from given package and import them into Supernova"
  static override examples = [
    "<%= config.bin %> <%= command.id %> --importFrom components-package --rootDir ./src",
    "<%= config.bin %> <%= command.id %> --importFrom @mui/material",
  ]
  static override flags = {
    designSystemId: Flags.string({ description: "Import code components to design system of", required: true }),
    importFrom: Flags.string({
      char: "i",
      description: "Package or directory path to import components from",
      required: true,
    }),
    rootDir: Flags.string({
      char: "r",
      default: process.cwd(), // Defaults to the current directory
      description:
        "Root directory of the package where the import should resolve from. Defaults to the current directory",
    }),
    versionId: Flags.string({ description: "Import code components to version of", required: true }),
  }
  static override hidden = true

  get commandId(): string {
    return ImportComponents.id
  }

  get configSchema(): ZodType<ImportComponentsConfig> {
    return ImportComponentsConfigSchema
  }

  @SentryTraced()
  public async run(): Promise<void> {
    const { flags } = await this.parse(ImportComponents)

    const { designSystemId, importFrom, rootDir, versionId } = flags

    this.log("📦 Component Import Summary".green)
    this.log("========================")
    this.log(`Source: ${importFrom}`)
    this.log(`Root Directory: ${rootDir}`)

    const components = await analyzeComponents({ importFrom, rootDir })

    if (components.length > 0) {
      this.log("\nComponent Summary Table:")
      this.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")
      this.log("Component Name   Props.Num    Required     Props Names    ")
      this.log("──────────────────────────────────────────────────────────")

      for (const component of components) {
        const propsCount = Object.keys(component.properties).length
        const requiredCount = Object.values(component.properties).filter(prop => prop.required).length
        const propsNames = new Set(Object.values(component.properties).map(prop => prop.name))

        // Format each row with fixed width columns
        const name = component.exportName.padEnd(16, " ").slice(0, 16)
        const propsNum = String(propsCount).padStart(5, " ")
        const required = String(requiredCount).padStart(10, " ")
        const props = [...propsNames].slice(0, 2).join(", ") + (propsNames.size > 2 ? ", ..." : "")

        this.log(`${name} ${propsNum} ${required}  ${props}`)
      }

      this.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")
      this.log("Importing to Supernova...")
      const client = await this.apiClient()
      const result = await client.designSystems.versions.codeComponents.import(designSystemId, versionId, {
        codeComponents: components.map(c => ({
          ...c,
          persistentId: `${designSystemId}-${versionId}${c.exportName}}`,
        })),
      })
      this.log(`Total: ${result.created} components imported`.green)
    } else {
      this.log("\n⚠️  No components were imported".green)
    }
  }
}
